/**
 * The whole state of the opt-in form.
 *
 * Every language lives here at once — the tab only decides which slice the fields read and write,
 * so switching tabs never loses what was typed. Saving assembles the payload from all of them.
 *
 * The entry point's appearance and the two toast switches sit **beside** `texts`, not inside it:
 * an icon, a badge and an alert level read the same in every language. The JSON view carries all
 * of it, so a pasted document recreates the cadastro complete.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCreateOptInFeature } from './useCreateOptInFeature';
import { OptInKeyHeldByDeletedError } from '../Services/OptInFeatures.errors';
import { useUpdateOptInFeature } from './useUpdateOptInFeature';
import type { OptInFeature, OptInLanguage } from '../Types/OptInFeatures.types';
import type {
  OptInAppearanceDraft,
  OptInFeatureFormData,
  OptInFormErrors,
  OptInTextsDraft,
  OptInToastsDraft,
} from '../Utils/optInFeatureForm';
import type { OptInTextsContent } from '../Utils/textsJson';
import {
  contentFromForm,
  getTextProblems,
  getToastProblems,
  parseTexts,
  serializeTexts,
} from '../Utils/textsJson';
import {
  buildCreatePayload,
  buildUpdatePayload,
  createEmptyFormData,
  hasErrors,
  mapFeatureToFormData,
  slugifyKey,
  validateFormData,
} from '../Utils/optInFeatureForm';

interface UseOptInFeatureFormParams {
  /** The feature being edited, or `undefined` for a new one. */
  feature?: OptInFeature;
  onSaved: () => void;
}

export interface UseOptInFeatureForm {
  form: OptInFeatureFormData;
  language: OptInLanguage;
  setLanguage: (language: OptInLanguage) => void;
  isEditing: boolean;
  isSaving: boolean;
  /** Filled in only after the first save attempt, so a blank form does not open covered in red. */
  errors: OptInFormErrors;
  setField: <K extends keyof OptInFeatureFormData>(
    field: K,
    value: OptInFeatureFormData[K],
  ) => void;
  setKey: (value: string) => void;
  setText: (field: keyof OptInTextsDraft, value: string) => void;
  /**
   * Writes one field of the entry point's appearance.
   *
   * Separate from `setText` because these have **no language**: an icon and a badge are the same
   * in all three, and routing them through the active tab is how two tabs end up disagreeing
   * about a glyph.
   */
  setAppearance: <K extends keyof OptInAppearanceDraft>(
    field: K,
    value: OptInAppearanceDraft[K],
  ) => void;
  /**
   * Writes one of the two toast switches.
   *
   * Separate from `setText` for the same reason as `setAppearance`: the **type** has no language.
   * The four sentences the toasts carry do, and those go through `setText` like any other text.
   */
  setToast: <K extends keyof OptInToastsDraft>(field: K, value: OptInToastsDraft[K]) => void;
  /**
   * Everything still missing, across all three languages.
   *
   * Not a recommendation: an opt-in feature has no approval step to catch a half-translated
   * cadastro — saving is publishing — so an active feature cannot be saved while this has entries.
   *
   * A toast switched on with no title is in here too, and it is **first**, because it is the one
   * entry the kill switch does not excuse: the BFF answers `400` whatever `isActive` says.
   */
  problems: string[];
  /** Whether saving is allowed right now. */
  canSave: boolean;
  /** Why saving is blocked, in one sentence, or `null` while it is not. */
  blockedReason: string | null;
  /** The same content as a JSON document: the other view of the texts, kept in sync both ways. */
  json: string;
  /** The parser's own message while the document cannot be read, `null` while it can. */
  jsonError: string | null;
  /** Types into the JSON view. Invalid text is kept verbatim; only a readable document lands. */
  setJson: (raw: string) => void;
  save: () => void;

  /**
   * The `409` of a key held by a **deleted** feature, or `null`.
   *
   * It is not shown as a plain failure: the screen offers to restore that feature, because telling
   * somebody the key is taken by a row they cannot see, recreate or reach is the dead end this
   * exists to close.
   */
  keyHeldByDeleted: OptInKeyHeldByDeletedError | null;
  dismissKeyConflict: () => void;
}

export const useOptInFeatureForm = ({
  feature,
  onSaved,
}: UseOptInFeatureFormParams): UseOptInFeatureForm => {
  const [form, setForm] = useState<OptInFeatureFormData>(() =>
    feature ? mapFeatureToFormData(feature) : createEmptyFormData(),
  );
  const [language, setLanguage] = useState<OptInLanguage>('en');
  const [errors, setErrors] = useState<OptInFormErrors>({});

  const createFeature = useCreateOptInFeature();
  /** Set when create answered `409` naming a deleted feature. `null` the rest of the time. */
  const [keyHeldByDeleted, setKeyHeldByDeleted] = useState<OptInKeyHeldByDeletedError | null>(null);
  const updateFeature = useUpdateOptInFeature();

  // The edit route mounts the form before the feature arrives, so it is seeded when it does.
  useEffect(() => {
    if (feature) setForm(mapFeatureToFormData(feature));
  }, [feature]);

  const setField = useCallback(
    <K extends keyof OptInFeatureFormData>(field: K, value: OptInFeatureFormData[K]) => {
      setForm(current => ({ ...current, [field]: value }));
    },
    [],
  );

  const setKey = useCallback((value: string) => {
    setForm(current => ({ ...current, key: slugifyKey(value) }));
  }, []);

  const setText = useCallback(
    (field: keyof OptInTextsDraft, value: string) => {
      setForm(current => ({
        ...current,
        texts: { ...current.texts, [language]: { ...current.texts[language], [field]: value } },
      }));
    },
    [language],
  );

  const setAppearance = useCallback(
    <K extends keyof OptInAppearanceDraft>(field: K, value: OptInAppearanceDraft[K]) => {
      setForm(current => ({ ...current, appearance: { ...current.appearance, [field]: value } }));
    },
    [],
  );

  const setToast = useCallback(
    <K extends keyof OptInToastsDraft>(field: K, value: OptInToastsDraft[K]) => {
      setForm(current => ({ ...current, toasts: { ...current.toasts, [field]: value } }));
    },
    [],
  );

  const [json, setJsonText] = useState<string>(() => serializeTexts(contentFromForm(form)));
  const [jsonError, setJsonError] = useState<string | null>(null);

  /** Set while a form change came from the JSON view, so the text is not rewritten under the cursor. */
  const isEditingJson = useRef(false);

  /**
   * The fields are the source of the document.
   *
   * Any change to the texts regenerates the JSON — except the ones the JSON view itself made,
   * which would otherwise reformat what someone is in the middle of typing.
   */
  useEffect(() => {
    if (isEditingJson.current) {
      isEditingJson.current = false;

      return;
    }

    setJsonText(
      serializeTexts({ texts: form.texts, appearance: form.appearance, toasts: form.toasts }),
    );
    setJsonError(null);
  }, [form.appearance, form.texts, form.toasts]);

  const applyTexts = useCallback((content: OptInTextsContent) => {
    setForm(current => ({
      ...current,
      texts: content.texts,
      appearance: content.appearance,
      toasts: content.toasts,
    }));
  }, []);

  const setJson = useCallback(
    (raw: string) => {
      setJsonText(raw);

      const result = parseTexts(raw);

      if (!result.ok) {
        setJsonError(result.message);

        return;
      }

      setJsonError(null);
      isEditingJson.current = true;
      applyTexts(result.content);
    },
    [applyTexts],
  );

  const textProblems = useMemo(() => getTextProblems(contentFromForm(form)), [form]);
  const toastProblems = useMemo(() => getToastProblems(contentFromForm(form)), [form]);

  /** The blocking ones first: they are the ones switching the feature off will not excuse. */
  const problems = useMemo(
    () => [...toastProblems, ...textProblems],
    [textProblems, toastProblems],
  );

  /**
   * An incomplete feature can still be saved **switched off**.
   *
   * Otherwise someone who has filled in half a cadastro has nowhere to put it. Off, it is in
   * nobody's bootstrap, so a missing translation reaches no one; on, it would.
   *
   * A toast with a type and no title is the exception, and it has to be: the BFF validates that
   * pair on the write, not on delivery, so "desative para salvar assim mesmo" would walk somebody
   * into a `400` instead of around it.
   */
  const canSave = toastProblems.length === 0 && (textProblems.length === 0 || !form.isActive);

  const blockedReason = useMemo(() => {
    if (toastProblems.length > 0) {
      return `${toastProblems.length === 1 ? 'Um toast está ligado' : `${toastProblems.length} toasts estão ligados`} sem título. Desativar a funcionalidade não contorna isso — o BFF recusa o cadastro de qualquer jeito. Escreva o título em inglês, ou volte o tipo para «Nenhum»: os textos ficam guardados.`;
    }

    if (textProblems.length > 0 && form.isActive) {
      return `${textProblems.length} ${textProblems.length === 1 ? 'pendência' : 'pendências'} acima. Resolva, ou desative a funcionalidade para salvar como rascunho — desativada ela não chega a ninguém.`;
    }

    return null;
  }, [form.isActive, textProblems.length, toastProblems.length]);

  const save = useCallback(() => {
    if (!canSave) return;

    const found = validateFormData(form);
    setErrors(found);

    if (hasErrors(found)) return;

    if (feature) {
      updateFeature.mutate(
        { id: feature.id, payload: buildUpdatePayload(form) },
        { onSuccess: onSaved },
      );

      return;
    }

    createFeature.mutate(buildCreatePayload(form), {
      onSuccess: onSaved,
      onError: error => {
        /*
          The key is held by a DELETED feature — the one conflict with a way out. The screen offers
          to restore that row instead of showing a wall of text about something nobody can see.
        */
        if (error instanceof OptInKeyHeldByDeletedError) setKeyHeldByDeleted(error);
      },
    });
  }, [canSave, createFeature, feature, form, onSaved, updateFeature]);

  return useMemo(
    () => ({
      form,
      language,
      setLanguage,
      isEditing: Boolean(feature),
      isSaving: createFeature.isPending || updateFeature.isPending,
      errors,
      setField,
      setKey,
      setText,
      setAppearance,
      setToast,
      problems,
      canSave,
      blockedReason,
      json,
      jsonError,
      setJson,
      save,
      keyHeldByDeleted,
      dismissKeyConflict: () => {
        setKeyHeldByDeleted(null);
      },
    }),
    [
      blockedReason,
      canSave,
      createFeature.isPending,
      errors,
      feature,
      form,
      json,
      jsonError,
      keyHeldByDeleted,
      language,
      problems,
      save,
      setAppearance,
      setJson,
      setField,
      setKey,
      setText,
      setToast,
      updateFeature.isPending,
    ],
  );
};
